import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from "@phosphor-icons/react";
import { SiteNav } from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

const INK = '#2A1E5C';
const BODY = '#413C52';
const MUTED = '#8B7E6A';
const GOLD = '#F5CB5C';
const CREAM = '#FDFBF7';

const Para = ({ children }) => (
  <p className="font-body text-lg leading-[1.85]" style={{ color: BODY }}>{children}</p>
);

const Heading = ({ children }) => (
  <h2
    className="font-heading text-2xl sm:text-3xl font-medium leading-tight mt-16 mb-6"
    style={{ color: INK }}
  >
    {children}
  </h2>
);

const FounderStoryPage = () => {
  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="founder-story-page">
      <SiteNav />

      {/* Back to About */}
      <div className="px-4 pt-6 sm:pt-8">
        <div className="max-w-2xl mx-auto flex items-center justify-end">
          <Link to="/about" className="font-body text-sm font-semibold underline" style={{ color: INK }} data-testid="founder-story-back">
            ← Back to About
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-10 pb-4 sm:pt-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="font-heading text-4xl sm:text-5xl font-medium leading-[1.1] mb-10" style={{ color: INK }} data-testid="founder-story-title">
            My neurodivergence helped me build my own finish line so I could stop overworking.
          </h1>
          <div className="flex justify-center">
            <img
              src="/images/6e297dc0-fd06-448b-b967-2f4b5a6c4c86.jpeg"
              alt="Shari Dove Shepard"
              className="w-56 h-56 sm:w-72 sm:h-72 rounded-full object-cover shadow-xl"
              style={{ border: `4px solid ${GOLD}` }}
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="px-4 sm:px-6 lg:px-8 pb-10">
        <div className="max-w-2xl mx-auto">

          {/* Intro */}
          <div className="space-y-6 pt-10">
            <Para>
              My dad used to tell me to get back on the horse. I always did. What I couldn’t explain to
              him was that I kept landing in the same place: capable, trying hard, and somehow unable to
              make the way I worked fit the life I wanted.
            </Para>
            <Para>
              For years, I thought the answer was to try harder. It took me much longer to ask whether I
              was trying to solve the wrong problem.
            </Para>
          </div>

          {/* I wasn't short on ambition */}
          <Heading>I wasn’t short on ambition</Heading>
          <div className="space-y-6">
            <Para>
              I earned a music scholarship to study violin, then gave it back when I couldn’t keep up with
              the workload. I was nearly deployed with the Peace Corps twice and withdrew both times.
              Later, I passed on graduate programs because I was afraid I couldn’t sustain the work I’d
              need to repay the loans.
            </Para>
            <Para>
              From the outside, those decisions could look like a lack of follow-through. From the inside,
              I was watching opportunities I wanted become impossible to manage, without knowing why.
            </Para>
            <Para>
              I was diagnosed with inattentive ADD at 33. I had begun looking for answers after finding
              webcomics that described the maddening experience of being offered a solution to a problem
              you don’t actually have. That was familiar. I didn’t need another reminder to care, or
              another lecture about effort.
            </Para>
            <Para>I needed to understand what was happening.</Para>
          </div>
        </div>

        {/* Hyperbole and a Half comic panels — kept together, in order, with attribution + link */}
        <div className="max-w-4xl mx-auto mt-12 mb-4">
          <p className="font-body text-sm text-center mb-6 uppercase tracking-wider" style={{ color: MUTED }}>
            From{' '}
            <a
              href="https://hyperboleandahalf.blogspot.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-80"
              style={{ color: MUTED }}
            >
              Hyperbole and a Half
            </a>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-3 justify-center items-center">
            <img loading="lazy" src="/images/1.webp" alt="Comic panel 1: What's wrong? My fish are dead." className="w-full max-w-sm sm:max-w-xs rounded-lg shadow-md" />
            <img loading="lazy" src="/images/2.webp" alt="Comic panel 2: Don't worry, I'll help you find them!" className="w-full max-w-sm sm:max-w-xs rounded-lg shadow-md" />
            <img loading="lazy" src="/images/3.webp" alt="Comic panel 3: That solution is for a different problem than the one I have." className="w-full max-w-sm sm:max-w-xs rounded-lg shadow-md" />
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* The cost of not knowing */}
          <Heading>The cost of not knowing</Heading>
          <div className="space-y-6">
            <Para>
              Before my diagnosis, I carried a quiet sense of failure from one job and decision to the
              next. A breaking point came in Seattle, where I was working two and a half jobs for $15 an
              hour. When my boss fired me, she said she had to worry about the quality of my work.
            </Para>
            <Para>
              I remember that sentence. I also remember how determined I was to get evaluated afterward.
            </Para>
            <Para>
              The diagnosis didn’t make everything easy. It gave me a better question. Instead of{' '}
              <em>Why can’t I make myself work like everyone else?</em> I could ask,{' '}
              <em>What kind of structure helps me work well?</em>
            </Para>
            <Para>
              I began building systems around that question. I wanted freedom, but I also needed enough
              structure to move forward without treating every difficult week as a verdict on who I was.
            </Para>
          </div>

          {/* Freedom needed a structure, too */}
          <Heading>Freedom needed a structure, too</Heading>
          <div className="space-y-6">
            <Para>
              Remote gig work eventually let me spend a year living in the woods, study herbs, and move to
              Mexico with my cat, Juju. I loved the freedom. I had also taken on a new kind of
              uncertainty: my income depended on work I had to find, pace, and manage myself.
            </Para>
            <Para>
              I wanted to build a life here and pursue residency. That gave my earnings a real purpose.
              But without a regular paycheck, I couldn’t easily tell what I needed to make in a week, what
              portion belonged to taxes and other goals, or when I could close my laptop and be done for
              the day.
            </Para>
          </div>
        </div>

        {/* Juju photo */}
        <figure className="max-w-md mx-auto mt-12 flex flex-col items-center">
          <img
            loading="lazy"
            src="/images/8870b38b-d345-4167-8249-0184624e98b6.jpeg"
            alt="Juju the cat looking out the window in Mexico"
            className="w-64 h-64 sm:w-72 sm:h-72 rounded-full object-cover shadow-lg"
          />
          <figcaption className="font-body text-sm text-center mt-3 italic" style={{ color: MUTED }}>
            Juju, enjoying our new life in Mexico.
          </figcaption>
        </figure>

        <div className="max-w-2xl mx-auto">
          {/* So I gave the week a number */}
          <Heading>So I gave the week a number</Heading>
          <div className="space-y-6">
            <Para>
              I started with a calculator. I entered what I needed my income to cover and worked backward
              to a weekly target. Then I gave each week its own beginning and end: one relay at a time.
            </Para>
            <Para>
              That changed the question from <em>Am I doing enough?</em> to{' '}
              <em>What am I working toward this week?</em> I could see my progress without asking my brain
              to hold the whole year at once. I still decided when to work and when to stop, but I had
              numbers to help me make those decisions.
            </Para>
          </div>
        </div>

        {/* Crochet photo */}
        <figure className="max-w-sm mx-auto mt-12 flex flex-col items-center">
          <img
            loading="lazy"
            src="/images/3cd4a6ea-5c14-4082-9d76-1b279179342c.webp"
            alt="Dove crocheting"
            className="w-full max-w-xs rounded-2xl shadow-lg"
          />
        </figure>

        <div className="max-w-2xl mx-auto">
          <div className="space-y-6 mt-12">
            <Para>
              Within three months of using the system, I had saved enough to take a vacation without
              working a single day. I was in El Zonte, genuinely unplugged. That mattered to me more than
              a perfect streak or a productivity score.
            </Para>
          </div>
        </div>

        {/* El Zonte photo */}
        <figure className="max-w-md mx-auto mt-12 flex flex-col items-center">
          <img
            loading="lazy"
            src="/images/el-zonte.webp"
            alt="Dove on vacation in El Zonte, taking a well-deserved break"
            className="w-full max-w-sm rounded-2xl shadow-lg"
          />
          <figcaption className="font-body text-sm text-center mt-3 italic" style={{ color: MUTED }}>
            A well-deserved break in El Zonte.
          </figcaption>
        </figure>

        <div className="max-w-2xl mx-auto">
          <div className="space-y-6 mt-12">
            <Para>
              The system became Payday Relay. It’s a way to turn the life you’re trying to fund into a
              weekly number, track what you earn, and make room for things that are easy to postpone when
              work has no natural stopping point.
            </Para>
          </div>

          {/* You can start with your own number */}
          <Heading>You can start with your own number</Heading>
          <div className="space-y-6">
            <Para>
              Maybe you know when you want to close your laptop but aren’t sure what your work needs to
              earn. Maybe the workday keeps stretching because you can’t tell when you’ve done enough.
              Both are real starting points.
            </Para>
            <Para>
              You don’t have to work the way I do. That’s the point. Start with your own numbers and see
              what a workable week might look like for you.
            </Para>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Link
              to="/start/free-sprint"
              className="inline-flex items-center gap-3 px-10 py-5 rounded-full font-heading font-bold text-lg transition-transform hover:scale-105"
              style={{ background: GOLD, color: INK, boxShadow: '0 8px 30px rgba(245, 203, 92, 0.4)' }}
              data-testid="founder-story-cta"
            >
              Find Your Weekly Relay
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="space-y-6 mt-14">
            <Para>
              I still think about my dad’s advice. I did get back up, many times. Eventually, I learned I
              was allowed to build a different way forward.
            </Para>
          </div>
        </div>

        {/* Final photo in Mexico */}
        <figure className="max-w-md mx-auto mt-14 flex flex-col items-center">
          <img
            loading="lazy"
            src="/images/6b54ea3e-c64e-4b46-bcbc-18b1e1f2b125.jpeg"
            alt="Dove living her best life in Mexico"
            className="w-full max-w-sm rounded-2xl shadow-lg"
          />
          <figcaption className="font-body text-sm text-center mt-3 italic" style={{ color: MUTED }}>
            Living my life on my own terms, one relay at a time.
          </figcaption>
        </figure>
      </article>

      <SiteFooter />
    </div>
  );
};

export default FounderStoryPage;
